// BesideU Admin dashboard API contracts — slice 12, DESIGN §6.6.
// Metadata-only by construction — no case content / Maeve conversations /
// policy documents shape leaks through these types.

import { z } from 'zod';
import { CGC_MEMBER_KINDS, type PromptApprovalMode } from './maeve.ts';
import { type Equals } from './type-utils.ts';

export const ORG_LIFECYCLE_STATUSES = ['Pending', 'Active', 'Suspended'] as const;
export type OrgLifecycleStatus = (typeof ORG_LIFECYCLE_STATUSES)[number];

// Slice 33 — per-org settings shape surfaced to the admin UI. Mirrors
// `OrganizationSettings` in `@besideu/db` but split into the raw
// override (what's in the JSON column) + the effective resolved value
// (override → env-var default → hard-coded fallback per
// `resolveAmberAggregationMinPool`). The admin UI shows both so an
// operator can tell at a glance whether the org is on the global
// default or a bespoke override.
export interface AdminOrgSettings {
  amberAggregationMinPool: {
    /** Raw per-org override; null when the org is on the env/fallback. */
    override: number | null;
    /** Resolved value the runtime actually uses. */
    effective: number;
    /** Where `effective` came from. */
    source: 'override' | 'env' | 'fallback';
  };
}

// Org list row — one per customer, metadata only.
export interface AdminOrgSummary {
  id: string;
  name: string;
  status: OrgLifecycleStatus;
  createdAt: string; // ISO 8601
  dpaVersionAccepted: string | null;
  dpaAcceptedAt: string | null;
  hrSafetyContactUserId: string | null;
  hrSafetyContactName: string | null;
  // Two-step nominate→accept (DESIGN.md — a reassignment is "gated on the
  // receiving user completing the acknowledgement step"). Set by
  // POST /orgs/:id/safety-contact ("nominate"); `hrSafetyContactUserId`
  // above only changes once the nominee accepts.
  pendingHrSafetyContactUserId: string | null;
  pendingHrSafetyContactName: string | null;
  pendingHrSafetyContactNominatedAt: string | null; // ISO 8601
  hrAdminCount: number;
  managerCount: number;
  employeeCount: number;
  caseCountOpen: number;
  caseCountClosed: number;
  settings: AdminOrgSettings;
  // Slice 37 — true when this org was provisioned by a BesideU Admin and its
  // invited HR Admin has not yet completed the gated first-sign-in onboarding
  // (status `Pending` + `provisionedByAdminId` set + DPA not yet accepted). The
  // admin UI uses this to render an "Awaiting HR onboarding" pill + the
  // "Resend HR invite" affordance, distinct from a self-serve Pending org
  // (whose HR Admin already accepted the DPA at the public signup form).
  awaitingHrOnboarding: boolean;
  // PROJECT §4 Step 1 — onboarding captures surfaced to operators. Null when
  // the org was created by a BesideU Admin and the HR Admin hasn't yet
  // completed gated first-sign-in onboarding. The detail view renders these
  // so an operator can confirm Step 1 was actually captured.
  headcountBand: string | null;
  primaryContactName: string | null;
  primaryContactEmail: string | null;
  escalationContactName: string | null;
  escalationContactEmail: string | null;
}

// Slice 33 — body shape for `PATCH /api/admin/orgs/:id/settings`. Today
// the only writable setting is `amberAggregationMinPool`. The full
// integer-or-null shape (null clears the override; integer >= 1 sets a
// per-org override that wins over the env default). Wire-shape `null`
// is preserved through Zod via `.nullable()` rather than `.optional()`
// — explicit clear vs. "field not present" is meaningful audit context.
export const UpdateOrgSettingsSchema = z.object({
  amberAggregationMinPool: z.number().int().min(1).max(10_000).nullable(),
});
export type UpdateOrgSettingsBody = z.infer<typeof UpdateOrgSettingsSchema>;

// Slice 37 — body for `POST /api/admin/orgs` (admin-provisioned org + first HR
// Admin). No DPA acceptance or safety-contact acknowledgement here: those are
// legally the data controller's to make, so the invited HR Admin supplies them
// at the gated first-sign-in onboarding step, not the operator at creation.
// Field bounds mirror `SignupRequestSchema` (org name 2–120, person 2–120,
// email lowercased + max 254) so the two onboarding paths stay congruent.
//
// PROJECT §4 Step 1 — headcount band + primary/escalation contacts. Optional
// here: an operator may pre-fill when the customer supplied the data out of
// band, but the org stays `Pending` until the HR Admin finishes the gated
// first-sign-in step, which is where these fields become effectively required
// (the HR-onboarding schema enforces presence; see `HrOnboardingSchema`).
import { headcountBandSchema, orgContactSchema, type OrgContact } from './signup.ts';
export const CreateOrgSchema = z.object({
  organizationName: z.string().trim().min(2).max(120),
  hrAdminName: z.string().trim().min(2).max(120),
  hrAdminEmail: z.string().trim().toLowerCase().email().max(254),
  headcountBand: headcountBandSchema.optional(),
  primaryContact: orgContactSchema.optional(),
  escalationContact: orgContactSchema.optional(),
});
export type CreateOrgRequest = z.infer<typeof CreateOrgSchema>;
export type CreateOrgOrgContact = OrgContact;

// HR_Admin user for the safety-contact-reassignment picker. Mirrors
// `UserSummary` but trimmed — the admin dashboard only needs id + name + email
// to drive the picker.
export interface AdminHrAdminCandidate {
  id: string;
  name: string;
  email: string;
  isCurrentSafetyContact: boolean;
  isPendingSafetyContact: boolean;
  hrSafetyContactAcknowledgedAt: string | null;
}

export const ADMIN_AUDIT_ACTIONS = [
  'Org_Suspended',
  'Org_Reactivated',
  // Two-step nominate→accept (DESIGN.md — see `hr_safety_contact_audit_events`
  // for the full lifecycle, incl. tenant-initiated nominations/acceptances
  // this admin-scoped log doesn't cover). `Safety_Contact_Reassigned` fires
  // on nomination (kept as the pre-existing string — renaming would touch
  // every historical row's meaning); accept can only happen tenant-side (an
  // admin can't accept on someone's behalf), so there's no admin-side
  // "accepted" action.
  'Safety_Contact_Reassigned',
  'Safety_Contact_Nomination_Cancelled',
  'Pending_Request_Approved',
  'Pending_Request_Rejected',
  // Slice 14 — system-prompt drafting actions. The BesideU Admin authors and
  // revises rows in `system_prompts`; CGC members approve them via the
  // governance UI (Slice 12); CGC members activate (Slice 13). All three
  // lifecycle stages have their own audit surfaces — drafting goes here,
  // approval lives in `system_prompt_decisions`, activation is reconstructable
  // from `system_prompts.activated_at` / `deactivated_at`.
  'PromptDraft_Created',
  'PromptDraft_Revised',
  'PromptDraft_Edited',
  // Slice 16 — operator-triggered re-ingestion of policy R2 binaries through
  // the extraction + embedding pipeline. Used for backfilling pre-pipeline
  // uploads and for re-embedding after a model rotation. Recorded so the
  // audit history shows when (and by whom) cross-tenant ingestion work ran.
  'Policy_Reingest',
  // Slice 19 — operator-triggered requeue of `Unavailable` (and optionally
  // `Failed`) policy audits, used after legal counsel signs off the three
  // `Policy_Audit_*` prompts and the pre-signoff uploads need re-running.
  // Recorded so the audit history shows when (and by whom) the bulk requeue
  // ran across tenants.
  'Policy_Audit_Requeue',
  // CGC member lifecycle (Slice 23+). Suspension is the offboarding mechanism
  // since RESTRICT FKs on `system_prompt_decisions.cgcMemberId` prevent hard
  // delete of any member who has approved a prompt. A suspended member can't
  // sign in, can't be `loadActor`-resolved, and is excluded from incident
  // review fan-out. Reactivate flips it back.
  'CGC_Member_Suspended',
  'CGC_Member_Reactivated',
  // CGC member onboarding + editing. Admin-driven invite (replaces the
  // pre-Slice-25 `wrangler d1 execute` bootstrap) issues a magic link to the
  // adviser, who completes TOTP enrolment on first sign-in. `Edited` records
  // kind / modesAllowed changes after invite. `InviteResent` re-dispatches the
  // magic link if the original expired pre-enrolment. `Revoked` hard-deletes
  // a member that has never enrolled AND has zero decisions on file — safe
  // because the RESTRICT FK on `system_prompt_decisions.cgcMemberId` becomes
  // load-bearing only after the first approve/reject; pre-enrolment members
  // cannot have written any decision row.
  'CGC_Member_Invited',
  'CGC_Member_Edited',
  'CGC_Member_InviteResent',
  'CGC_Member_Revoked',
  // Slice 35 — placeholder lifecycle. `Identity_Updated` is the admin-log
  // index of a rename / email change (the full before/after snapshot lives
  // in the `cgc_member_identity_changes` table; the admin log carries just
  // the pointer + reason so cross-action timelines still surface it).
  // `Promoted_To_Active` is the one-way `Placeholder → Active` lifecycle
  // event (mirrors `CGC_Member_Reactivated` for the suspend lifecycle).
  'CGC_Member_Identity_Updated',
  'CGC_Member_Promoted_To_Active',
  // Slice 31 — governance allocations + settings writes. `Role_Assigned`
  // covers both initial assignment and reassignment; `Setting_Updated`
  // covers all free-text setting writes (DPIA scope etc.).
  'Governance_Role_Assigned',
  'Governance_Setting_Updated',
  // Slice 33 — per-org settings editor. Today this is just the
  // `amber_aggregation_min_pool` override (Safety Guide Q7) but the
  // action name is scoped to the full settings object so future
  // additions don't need a new audit action.
  'Org_Settings_Updated',
  // Slice 34 — QA scoring rubric (DESIGN §9.4 #5).
  //
  // Only the admin-driven action (sample creation) lives in this audit
  // table. CGC-driven actions — per-dimension rubric review and
  // per-sample scoring — are persisted as rows in
  // `qa_rubric_dimension_reviews` / `qa_rubric_sample_scores`; those
  // tables ARE the audit trail (same posture as
  // `classifier_category_reviews`). The admin log's FK is
  // admin-actor-only, so a CGC actor doesn't fit cleanly here.
  'QA_Sample_Created',
  // QA sampler redactor slice — admin spot-check decisions on auto-redacted
  // session snapshots. Approve writes a `qa_rubric_samples` row pointing
  // back at the originating snapshot via `originSnapshotId`; reject flips
  // the snapshot status to `rejected` with a free-text reason. The
  // snapshot row itself carries the redactor's draft + confidence + flags;
  // this audit row carries the admin's decision, the sample id (approve
  // path) or rejection reason (reject path), and the actor pointer.
  'QA_Redaction_Approved',
  'QA_Redaction_Rejected',
  // Right-to-erasure (Article 17) action on the QA-sampler + Companion-DO
  // tracks. Run by `POST /api/admin/employees/:id/erase-companion-data` —
  // the focused QA-cleanup half of an RTBF flow (a wider user-row
  // deletion is its own action when it ships). Payload carries the
  // `{consentRows, snapshotRows, rubricSampleRows}` counts from
  // `clearAllConsentForEmployee` plus a `doWiped: boolean` flag for the
  // Companion DO purge.
  'Employee_Companion_Data_Erased',
  // Slice 36 — admin-on-admin lifecycle. Platform admins can now invite other
  // platform admins (replacing the `wrangler d1 execute` bootstrap as the sole
  // provisioning path). Same posture as the `CGC_Member_*` quartet: invite
  // issues a magic link; the invitee completes TOTP enrolment on first sign-in.
  // Suspension (not hard-delete) is the offboarding mechanism — the RESTRICT FK
  // on `besideu_admin_audit_log.actorAdminId` keeps any admin who has acted in
  // the audit trail forever. There is no `Revoked` action for admins: a never-
  // enrolled admin invite is still revoke-able only via Suspend, to keep the
  // self-referential audit-actor chain intact.
  'Admin_Invited',
  'Admin_InviteResent',
  'Admin_Suspended',
  'Admin_Reactivated',
  // Slice 37 — admin-provisioned org onboarding. A BesideU Admin can create an
  // org + its first HR Admin from the dashboard (an operator/sales-led path
  // alongside HR self-serve signup). The org is created `Pending` with NULL
  // DPA / safety-contact fields; the invited HR Admin completes the gated
  // first-sign-in onboarding (`/auth/onboarding/complete`) which accepts the
  // DPA + acknowledges safety-contact and flips the org `Active`. These actions
  // group under `org` in the audit-log filter (same as the other `Org_*`).
  'Org_Created',
  'Org_HR_Admin_Invited',
  'Org_HR_Admin_InviteResent',
] as const;
export type AdminAuditAction = (typeof ADMIN_AUDIT_ACTIONS)[number];

// CGC member admin-list row. Surfaced by `GET /api/admin/cgc-members` so the
// BesideU Admin can see the full roster (including suspended members — the
// CGC-side `getById` hides them, but the admin needs to see them to
// reactivate). Metadata only: no TOTP secret, no decision content.
//
// Slice 35 — `Placeholder` is a stub identity used in staging engineering
// (and pre-pilot production) before the real adviser-of-record paperwork
// closes. Behaves like Active for auth + workflow purposes; refused only
// at the `SystemPromptLoader` env-gate (activation / serving in production).
// `promotedToActiveAt` / `promotedByAdminId` / `promotedByAdminName` are
// populated after `POST /api/admin/cgc-members/:id/promote` flips a
// Placeholder to Active — one-way lifecycle, mirrors the suspend triplet.
export interface AdminCgcMemberSummary {
  id: string;
  name: string;
  email: string;
  kind: string;
  modesAllowed: readonly string[];
  status: 'Active' | 'Suspended' | 'Placeholder';
  totpEnrolled: boolean;
  lastSignInAt: string | null;
  suspendedAt: string | null;
  suspendedByAdminId: string | null;
  suspendedByAdminName: string | null;
  promotedToActiveAt: string | null;
  promotedByAdminId: string | null;
  promotedByAdminName: string | null;
  decisionCount: number;
  createdAt: string;
}

// Slice 36 — platform-admin roster row. Surfaced by `GET /api/admin/admins`
// so a BesideU Admin can see every operator (including suspended ones, which
// the auth-side `findActiveByEmail` hides — the roster needs them visible to
// reactivate). Metadata only: no TOTP secret. Mirrors `AdminCgcMemberSummary`
// minus the CGC-specific fields (kind / modesAllowed / decisionCount /
// placeholder-promotion triplet). `totpEnrolled` distinguishes an invited-but-
// never-signed-in admin (amber "Awaiting first sign-in") from a live one.
export interface AdminMemberSummary {
  id: string;
  name: string;
  email: string;
  status: 'Active' | 'Suspended';
  totpEnrolled: boolean;
  lastSignInAt: string | null;
  suspendedAt: string | null;
  suspendedByAdminId: string | null;
  suspendedByAdminName: string | null;
  // True for the admin making the request — the UI uses this to disable the
  // self-suspend affordance (an admin must not lock themselves out; another
  // admin has to do it). Defence-in-depth only; the server refuses self-
  // suspend independently.
  isSelf: boolean;
  createdAt: string;
}

// Invite a new platform admin. Far simpler than `InviteCgcMemberSchema` — no
// `kind`, no `modesAllowed`, no `status` (admins are always invited Active;
// there's no Placeholder concept for an internal operator).
export const InviteAdminSchema = z.object({
  name: z.string().min(1).max(200),
  email: z.string().email().toLowerCase(),
});
export type InviteAdminRequest = z.infer<typeof InviteAdminSchema>;

export interface AdminAuditEntry {
  id: string;
  action: AdminAuditAction;
  actorAdminId: string;
  actorAdminName: string | null; // null only if the admin row has been deleted (RESTRICT FK; never expected in practice)
  targetOrgId: string | null;
  targetOrgName: string | null;
  targetUserId: string | null;
  payload: Record<string, unknown> | null;
  createdAt: string;
}

// Request bodies — all action endpoints accept an empty body except for
// safety-contact reassignment, which carries the new user id.

export const ReassignSafetyContactSchema = z.object({
  userId: z.string().uuid(),
});
export type ReassignSafetyContactRequest = z.infer<typeof ReassignSafetyContactSchema>;

// CGC member invite + edit request schemas. Shape-only validation here; the
// "modes must be a subset of what `kind` is permitted to approve" rule is
// enforced in the repository against `PROMPT_MODE_APPROVAL_REQUIREMENTS`
// from `@besideu/shared/maeve` — keeping the business rule co-located with
// the kind/mode authority rather than restating it as a Zod refinement.
export const InviteCgcMemberSchema = z.object({
  name: z.string().min(1).max(200),
  email: z.string().email().toLowerCase(),
  kind: z.enum(CGC_MEMBER_KINDS),
  // Optional per-member allow-list override. Empty = use kind defaults
  // (see `canApproveMode` in `@besideu/shared/maeve`).
  modesAllowed: z.array(z.string()).optional().default([]),
  // Slice 35 — `Placeholder` invites the member as a stub identity for
  // pre-paperwork engineering. Default 'Active' preserves Slice-24 behaviour
  // (the existing UI form submits no `status` field). The
  // `SystemPromptLoader` env-gate refuses Placeholder approvals in
  // production; staging / dev accept them.
  status: z.enum(['Active', 'Placeholder']).optional().default('Active'),
});
export type InviteCgcMemberRequest = z.infer<typeof InviteCgcMemberSchema>;

export const UpdateCgcMemberSchema = z
  .object({
    kind: z.enum(CGC_MEMBER_KINDS).optional(),
    modesAllowed: z.array(z.string()).optional(),
  })
  // At least one field must be present — the route handler also re-checks,
  // but enforcing here surfaces a clean 400 instead of a no-op UPDATE.
  .refine((v) => v.kind !== undefined || v.modesAllowed !== undefined, {
    message: 'at least one of kind or modesAllowed must be provided',
  });
export type UpdateCgcMemberRequest = z.infer<typeof UpdateCgcMemberSchema>;

// Slice 35 — identity-change request (rename + email update). Distinct from
// `UpdateCgcMemberSchema` (which handles kind / modesAllowed) because:
//   • the audit posture is different — identity changes write to the
//     append-only `cgc_member_identity_changes` table, kind/mode edits write
//     to the generic admin audit log;
//   • the reason field is conditionally required (Active members only) and
//     it'd muddy the `UpdateCgcMemberSchema` semantics to mix the two.
//
// `reason` is optional in the schema and enforced at the repo layer based on
// the target member's current status (Active → required, Placeholder →
// optional). The route layer surfaces the repo's `reason_required_for_
// active_member` as a 400.
export const UpdateCgcMemberIdentitySchema = z
  .object({
    name: z.string().min(1).max(200).optional(),
    email: z.string().email().toLowerCase().optional(),
    reason: z.string().min(1).max(500).optional(),
  })
  .refine((v) => v.name !== undefined || v.email !== undefined, {
    message: 'at least one of name or email must be provided',
  });
export type UpdateCgcMemberIdentityRequest = z.infer<typeof UpdateCgcMemberIdentitySchema>;

// Slice 35 — promote a Placeholder to Active. One-way lifecycle.
export const PromoteCgcMemberSchema = z.object({
  reason: z.string().max(500).optional(),
});
export type PromoteCgcMemberRequest = z.infer<typeof PromoteCgcMemberSchema>;

// Slice 35 — identity-change log entry surfaced by
// `GET /api/admin/cgc-members/:id/identity-changes`.
export interface CgcMemberIdentityChangeEntry {
  id: string;
  oldName: string;
  newName: string;
  oldEmail: string;
  newEmail: string;
  actorAdminId: string | null;
  actorAdminName: string | null;
  reason: string | null;
  createdAt: string;
}

// =============================================================================
// Slice 14 — system-prompt drafting (DESIGN §5.5, §9.3 #11).
//
// The BesideU Admin's authoring surface. Mirrors the loader's column shape
// minus the approval/activation columns — those belong to the CGC governance
// repository (Slice 12 + 13). Two-person rule enforced by file boundary: the
// admin-prompts repo never imports cgc-governance.ts and never writes
// `approved_by` / `approved_jointly_by` / `is_active` / `activated_at` /
// `deactivated_at`. Even if a future change accidentally wires this schema
// into a code path that touches those columns, the missing fields here
// surface as a TS error rather than a silent over-write.
// =============================================================================

// Prompt-mode enum is owned by `@besideu/db` (the schema's authoritative
// source) and re-exported via `@besideu/shared/maeve` for the boilerplate
// validator. We restate the strings here as a Zod enum.
//
// Drift is caught at compile time in BOTH directions:
//
//   1. `satisfies readonly PromptApprovalMode[]` (below) rejects any string
//      added to PROMPT_DRAFT_MODES that isn't a valid PromptApprovalMode
//      (typos, removed modes).
//   2. `_PROMPT_DRAFT_MODES_EXHAUSTIVE_CHECK` (also below) rejects any
//      PromptApprovalMode that is MISSING from PROMPT_DRAFT_MODES — the
//      sentinel is typed `never` iff the two sets are equal, so adding a
//      mode to PROMPT_MODE_APPROVAL_REQUIREMENTS without also adding it
//      here is a TS error.
//
// Together: the two lists are forced into exact equality at compile time.
export const PROMPT_DRAFT_MODES = [
  'HR_Maeve',
  'Manager_Guidance',
  'Employee_Companion',
  'Policy_Audit',
  // Slice 19 — Multi-Agent Policy Audit sub-prompts. Three sibling rows,
  // each independently approved by legal counsel; the audit pipeline reads
  // all three at runtime. (The legacy `Policy_Audit` above stays for
  // back-compat with pre-Slice-19 single-mode rows; the audit pipeline
  // does not read it.) Added to PROMPT_DRAFT_MODES so the admin UI can
  // author each sub-mode independently — gap caught by the exhaustiveness
  // check below.
  'Policy_Audit_Compliance',
  'Policy_Audit_Empathy',
  'Policy_Audit_Supervisor',
  'Trajectory_Classifier',
  'AI_Disclosure',
  'Companion_Wrap_Nudge',
  'Companion_Empathetic_Close',
  // Slice 27 — boilerplate-as-data. Authoring path mirrors any other Maeve
  // prompt: admin drafts here, legal counsel approves on the CGC dashboard,
  // activation flips the active version. The runtime validator
  // (`validateMaeveModeBoilerplate`) reads the active row of each
  // boilerplate mode when validating HR_Maeve / Manager_Guidance /
  // Employee_Companion drafts, falling back to the code constants in
  // `@besideu/shared/maeve` if no active row exists.
  'Boilerplate_Intended_Use',
  'Boilerplate_Human_Oversight',
  'Boilerplate_Prohibited_Use',
  // Slice 31 — Data Processing Agreement click-through copy. Authored via
  // `/admin/prompts` and approved by legal_counsel on the CGC dashboard,
  // same lifecycle as the Boilerplate_* modes. Signup flow reads the
  // active row's body + version via the loader, with code-constant
  // fallback to `@besideu/shared/signup.DPA_PLACEHOLDER_*` if no active
  // DPA row exists.
  'DPA',
  // DPIA (PROJECT §5 hard gate #3.1) — Data Protection Impact Assessment.
  // Authored via `/admin/prompts`, single legal_counsel sign-off on the CGC
  // dashboard, same lifecycle as DPA. Surfaced read-only in the admin
  // Governance panel (active row's body/version, with a code-constant
  // fallback to `DPIA_PLACEHOLDER_*`).
  'DPIA',
  // QA sampler foundation slice — LLM-as-judge prompt. Authored via
  // `/admin/prompts`, joint MHP + GP/psychiatrist sign-off on the CGC
  // dashboard. Same lifecycle as Trajectory_Classifier. Until activated,
  // session-end scoring short-circuits (scoreSessionWithJudge returns
  // prompt_not_found) so the feature ships dead-on-arrival and lights
  // up the moment governance approves.
  'QA_Judge',
  // QA sampler redactor slice — LLM-as-redactor prompt. Authored via
  // `/admin/prompts`, single legal_counsel sign-off on the CGC dashboard.
  // Same lifecycle as DPA / AI_Disclosure. Until activated, the redactor
  // batch processor short-circuits (every snapshot lands in
  // `pending_admin_review` with `flags: ['redactor_failed:prompt_not_found']`)
  // so the admin queue surfaces them with the regex-pre-pass draft only.
  'QA_Redactor',
  // PROJECT §4 Step 2 — policy key-rule extraction. Single legal_counsel
  // sign-off (matches DPA / AI_Disclosure / Boilerplate_* posture). On
  // policy upload the consumer loads the active row and asks Vertex for a
  // structured rules list; HR reviews the rules in the HR Policies UI
  // before the policy can be `Approved`. Until a row is approved by
  // legal counsel the consumer returns `Unavailable` and the policy
  // surfaces a "Awaiting CGC sign-off" pill rather than a confusing
  // error.
  'Policy_KeyRule_Extraction',
] as const satisfies readonly PromptApprovalMode[];

// Exhaustiveness check — fires a TS error if any PromptApprovalMode is
// missing from PROMPT_DRAFT_MODES (or vice versa). See `Equals` in
// `@besideu/shared/type-utils` for why the function-overload pattern is
// load-bearing over naïve alternatives. Drift surfaces as `Type 'true'
// is not assignable to type 'false'` pointing exactly at this line.
// The expression-statement form (no named binding) means there's no
// "unused variable" lint to half-delete during cleanup.
true satisfies Equals<PromptApprovalMode, (typeof PROMPT_DRAFT_MODES)[number]>;

const VERSION_PATTERN = /^\d+\.\d+\.\d+(?:[-+][0-9A-Za-z.-]+)?$/;

// Shared field constraints. Pulled out so create/revise/edit Zod schemas all
// validate identically — drift between the three has caused subtle bugs in
// adjacent slices (an admin could PATCH a value they couldn't POST).
const promptText = z.string().min(50).max(50_000);
const version = z.string().regex(VERSION_PATTERN, 'Version must be semver (e.g. 1.0.0)');
const changeSummary = z.string().min(1).max(2_000);
const countryCode = z.string().regex(/^[A-Z]{2}$/, 'ISO 3166-1 alpha-2 (e.g. UK)');

export const CreatePromptDraftSchema = z.object({
  mode: z.enum(PROMPT_DRAFT_MODES),
  countryCode: countryCode.default('UK'),
  organizationId: z.string().uuid().nullable().default(null),
  promptText,
  version,
  changeSummary: changeSummary.optional(),
});
export type CreatePromptDraftRequest = z.infer<typeof CreatePromptDraftSchema>;

export const RevisePromptDraftSchema = z.object({
  promptText,
  // The new version must differ from the previous-version's `version`.
  // Server-side uniqueness check enforces this; the regex here is just shape.
  version,
  changeSummary, // REQUIRED for revisions — load-bearing audit context.
});
export type RevisePromptDraftRequest = z.infer<typeof RevisePromptDraftSchema>;

export const EditPromptDraftSchema = z.object({
  // In-place edit of a pristine draft (no decisions yet). The server-side
  // gate refuses if ANY decision (approve OR reject) exists against the row.
  // Once any CGC member has acted, the row is immutable and the admin must
  // use the `revise` path instead.
  promptText: promptText.optional(),
  changeSummary: changeSummary.optional(),
});
export type EditPromptDraftRequest = z.infer<typeof EditPromptDraftSchema>;

// Response shapes. Mirrors `system_prompts` columns the admin is allowed to
// see (which is all of them — admins see the full picture including the CGC
// approval state, even though they can't write it). The shape is structurally
// distinct from the CGC governance API's `PromptListEntry` because the admin
// surface doesn't apply mode-scope filtering (admins see all modes) and
// doesn't compute the `'inactive'` status (that's a CGC-UI affordance for
// "this row was displaced — don't reactivate"; the admin sees it as just
// `isActive: false`).
export interface AdminPromptSummary {
  id: string;
  mode: (typeof PROMPT_DRAFT_MODES)[number];
  countryCode: string;
  organizationId: string | null;
  organizationName: string | null;
  version: string;
  promptTextPreview: string;
  isActive: boolean;
  approvedBy: string | null;
  approvedAt: string | null;
  approvedJointlyBy: string | null;
  approvedJointlyAt: string | null;
  activatedAt: string | null;
  deactivatedAt: string | null;
  previousVersionId: string | null;
  changeSummary: string | null;
  // Whether the row is still editable in-place (no CGC decisions filed
  // against it). Drives the admin UI's edit-vs-revise affordance.
  isPristine: boolean;
  decisionCount: number;
  createdAt: string;
  // Slice 30 — max(createdAt, MAX(decisions.createdAt),
  // MAX(audit.createdAt where targetPromptId = id), MAX(activations.createdAt))
  // — computed server-side via scalar subqueries so the admin list view
  // can sort by recent activity without a per-row history fetch. ISO 8601.
  lastActivityAt: string;
}

export interface AdminPromptDetail extends AdminPromptSummary {
  promptText: string;
}

// Slice 26 — per-prompt unified history timeline. Merges three sources in
// chronological order (oldest first): admin draft actions from
// `besideu_admin_audit_log`, CGC approve/reject decisions from
// `system_prompt_decisions`, and synthetic lifecycle events derived from
// the prompt row's own `activatedAt` / `deactivatedAt` columns. The three
// kinds intentionally carry different sets of fields because they answer
// different questions — drafting vs governance vs deployment — and we don't
// want a flat shape that lies about uniformity. The CGC `'activated'`
// event has no recorded actor: §6.1 documents activation as deliberately
// not writing a decisions row, and the `system_prompts` table has no
// `activatedByCgcMemberId` column.
export type AdminPromptHistoryEntry =
  | {
      kind: 'admin_action';
      at: string;
      action: 'PromptDraft_Created' | 'PromptDraft_Revised' | 'PromptDraft_Edited';
      actorAdminId: string;
      // Null only if the audit row's actor admin was deleted out-of-band —
      // the `actorAdminId` column is RESTRICT, so in normal operation this
      // is always populated.
      actorAdminName: string | null;
      payload: Record<string, unknown> | null;
    }
  | {
      kind: 'cgc_decision';
      at: string;
      decision: 'approved' | 'rejected';
      cgcMemberId: string;
      // Nullable for the same `PRAGMA foreign_keys = OFF` defence-in-depth
      // scenario as `actorAdminName` above. The `cgcMemberId` FK is RESTRICT
      // and the `revokeCgcMember` path also pre-checks `decisionCount === 0`,
      // so a deleted member with a decision row is unreachable in normal
      // operation — but `getHistory` uses a leftJoin to render `(deleted
      // member)` rather than silently dropping the row.
      cgcMemberName: string | null;
      cgcMemberKind: string | null;
      reason: string | null;
    }
  | {
      kind: 'lifecycle';
      at: string;
      event: 'activated' | 'deactivated';
      // Slice 29 — actor pointer + name. Nullable on the actor pointer
      // because pre-Slice-29 activations were backfilled into the audit
      // table without an actor (the `activatedAt` / `deactivatedAt`
      // columns historically carried no `cgcMemberId`). Post-Slice-29
      // CGC-driven activations always populate it. `actorName` is
      // separately nullable for the `PRAGMA foreign_keys = OFF` defence-
      // in-depth posture used elsewhere in the timeline (a deleted CGC
      // member with no decisions would unlock hard-delete, but the audit
      // table's RESTRICT FK protects this in normal operation).
      actorCgcMemberId: string | null;
      actorName: string | null;
      actorKind: string | null;
    };

// Slice 27 — `GET /api/admin/boilerplate` returns the three §4.7 boilerplate
// blocks resolved against active DB rows, with `source: 'constant'` indicating
// fallback to the `@besideu/shared/maeve` code constants. The admin UI uses
// this to render the inspector panel and to populate the "insert §4.7
// boilerplate" helper button in the prompt-text editors.
//
// `intended_use` and `human_oversight` are single-text blocks. `prohibited_use`
// carries an `anchors` array: when sourced from the constants, this is the
// three-phrase legacy list; when sourced from an active DB row, this is a
// one-element array containing the canonical body. The UI shows the first
// anchor as the canonical text to insert (or all three if `source === 'constant'`).
export interface BoilerplateBlock {
  readonly source: 'db' | 'constant';
  /** Single canonical text (intended_use, human_oversight) or null for anchor-list shape. */
  readonly text: string | null;
  /** Set only for `prohibited_use`. One element on DB-sourced, three on constant fallback. */
  readonly anchors: readonly string[] | null;
  /** The active row id, when `source === 'db'`. */
  readonly promptId: string | null;
  /** The active version, when `source === 'db'`. */
  readonly version: string | null;
}

export interface AdminBoilerplateResponse {
  readonly blocks: {
    readonly intended_use: BoilerplateBlock;
    readonly human_oversight: BoilerplateBlock;
    readonly prohibited_use: BoilerplateBlock;
  };
}

// =============================================================================
// Slice 31 — governance allocations + settings.
// =============================================================================

export const GOVERNANCE_ROLES = [
  'bacp_grief_counsellor',
  'mhp',
  'gp_psychiatrist',
  'legal_counsel',
  'dpia_owner',
  'testing_programme_owner',
] as const;
export type GovernanceRole = (typeof GOVERNANCE_ROLES)[number];

// Mapping each role to the CGC `kind` that is permitted to fill it when
// the role is assigned to a CGC member (vs an external person). Roles
// that allow ANY CGC kind (or non-CGC-member fills) map to null and the
// admin UI doesn't restrict the picker. `dpia_owner` and
// `testing_programme_owner` are open because they're commonly filled
// outside the CGC.
export const GOVERNANCE_ROLE_CGC_KIND_REQUIREMENT: Record<
  GovernanceRole,
  'grief_counsellor' | 'mhp' | 'gp_psychiatrist' | 'legal_counsel' | null
> = {
  bacp_grief_counsellor: 'grief_counsellor',
  mhp: 'mhp',
  gp_psychiatrist: 'gp_psychiatrist',
  legal_counsel: 'legal_counsel',
  dpia_owner: null,
  testing_programme_owner: null,
};

// One row per governance role. `assignment.kind = 'unassigned'` when
// neither a CGC member nor an external person has been allocated yet —
// the migration plants every role in this state so the admin UI can
// render an editable card per role from day one.
export type GovernanceAssignment =
  | { readonly kind: 'unassigned' }
  | {
      readonly kind: 'cgc_member';
      readonly cgcMemberId: string;
      readonly cgcMemberName: string;
      readonly cgcMemberEmail: string;
      readonly cgcMemberKind: string;
    }
  | { readonly kind: 'external'; readonly name: string; readonly email: string };

export interface GovernanceRoleEntry {
  readonly role: GovernanceRole;
  readonly assignment: GovernanceAssignment;
  readonly assignedAt: string | null;
  readonly assignedByAdminName: string | null;
}

// Scalar governance settings, one row per key in `governance_settings`.
// `dpia_scope` is free text; `qa_calibration_min_samples` is a positive
// integer (stored as text like every value column) — the calibration
// slice's per-(mode, dimension) paired-sample threshold for lifting the
// `uncalibrated` badge. Unset/invalid falls back to
// QA_CALIBRATION_DEFAULT_MIN_SAMPLES at read time.
export const GOVERNANCE_SETTING_KEYS = ['dpia_scope', 'qa_calibration_min_samples'] as const;
export type GovernanceSettingKey = (typeof GOVERNANCE_SETTING_KEYS)[number];

export interface GovernanceSettingEntry {
  readonly key: GovernanceSettingKey;
  readonly value: string | null;
  readonly updatedAt: string | null;
  readonly updatedByAdminName: string | null;
}

// Snapshot of the active DPA — sourced from `system_prompts` (DPA mode)
// via the loader, with code-constant fallback. The admin UI shows the
// current source + version so the operator can see at a glance whether
// the constant fallback is in effect or a CGC-approved DB row is live.
export interface GovernanceDpaState {
  readonly source: 'db' | 'constant';
  readonly version: string;
  readonly body: string;
  /** Active DB row id when source === 'db'. */
  readonly promptId: string | null;
}

// DPIA placeholder — the fallback shown in the Governance panel until legal
// counsel signs off a `DPIA`-mode `system_prompts` row. Labelled non-binding
// in the UI (same posture as the DPA placeholder). The `dpia_scope` governance
// setting captures scope/owner; this is the signed-off assessment document.
export const DPIA_PLACEHOLDER_VERSION = '0.1.0-placeholder';
export const DPIA_PLACEHOLDER_TEXT = `besideU placeholder Data Protection Impact Assessment v${DPIA_PLACEHOLDER_VERSION}. This is placeholder language pending legal-counsel review and sign-off; it is not the completed DPIA. The signed-off assessment will replace it before any production-pilot data is processed (PROJECT §5 hard gate #3.1).`;

export interface GovernanceOverview {
  readonly roles: readonly GovernanceRoleEntry[];
  readonly settings: readonly GovernanceSettingEntry[];
  readonly dpa: GovernanceDpaState;
  // Signed-off DPIA document — same shape/lifecycle as `dpa` (a `system_prompts`
  // row of mode 'DPIA', legal_counsel approved + active), with code-constant
  // fallback. PROJECT §5 hard gate #3.1.
  readonly dpia: GovernanceDpaState;
}

// Wire schema for the PUT endpoints. The role-assignment body is a
// discriminated union mirroring `GovernanceAssignment` — the admin UI
// sends either a `cgcMemberId` (internal allocation) or a `name+email`
// pair (external allocation) or `unassigned` to clear the row.
export const AssignGovernanceRoleBodySchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('unassigned') }),
  z.object({ kind: z.literal('cgc_member'), cgcMemberId: z.string().uuid() }),
  z.object({
    kind: z.literal('external'),
    name: z.string().trim().min(1).max(120),
    email: z.string().trim().toLowerCase().email().max(254),
  }),
]);
export type AssignGovernanceRoleBody = z.infer<typeof AssignGovernanceRoleBodySchema>;

export const UpdateGovernanceSettingBodySchema = z.object({
  value: z.string().max(50_000),
});
export type UpdateGovernanceSettingBody = z.infer<typeof UpdateGovernanceSettingBodySchema>;

// =============================================================================
// Slice 32 — classifier-category inspector + per-category CGC review log.
// =============================================================================

// One entry per row recorded in `classifier_category_reviews`.
export interface ClassifierCategoryReviewerRef {
  readonly cgcMemberId: string;
  readonly cgcMemberName: string;
  readonly cgcMemberKind: string;
  readonly reviewedAt: string;
  readonly notes: string | null;
}

// Per-category status surfaced to the admin inspector. `requiredKinds`
// mirrors `CGC_KINDS_FOR_INCIDENT[category]` from `@besideu/shared/maeve`;
// `jointSignOff` is `true` for clinical categories (both MHP + GP-psychiatrist
// must approve) and `false` for `safety_concerns` (single legal_counsel).
//
// `reviewers` is the list of approvals against the CURRENT `CLASSIFIER_SET_VERSION`
// only. Approvals against earlier versions are not surfaced here — they
// can be queried separately via the audit log if needed.
export type ClassifierCategoryReviewStatus =
  | { readonly kind: 'pending' }
  | {
      readonly kind: 'partially_reviewed';
      readonly missingKinds: readonly string[];
      readonly reviewers: readonly ClassifierCategoryReviewerRef[];
    }
  | {
      readonly kind: 'reviewed';
      readonly reviewers: readonly ClassifierCategoryReviewerRef[];
    };

export interface ClassifierCategoryInspectorEntry {
  readonly categoryKey: string;
  readonly displayName: string;
  readonly description: string;
  readonly guardrailCopy: string;
  readonly requiredKinds: readonly string[];
  readonly jointSignOff: boolean;
  readonly status: ClassifierCategoryReviewStatus;
  /**
   * Source of the per-category user-facing response guidance that
   * the Companion / Trajectory composers prepend to the system
   * instruction in non-prod. `stub` = the hard-coded fragment in
   * `packages/shared/src/maeve-category-stubs.ts` (dev/staging only).
   * `system_prompts` = an operator-supplied row in the
   * `system_prompts` table (the only source in production). The CGC
   * inspector surfaces this so a reviewer knows whether the wording
   * they sign off on in the inspector is the wording the model
   * actually sees at runtime.
   */
  readonly responseGuidanceSource: 'stub' | 'system_prompts';
}

export interface ClassifierCategoryInspectorResponse {
  /** `CLASSIFIER_SET_VERSION` at read time. */
  readonly setVersion: string;
  /**
   * The worker `NODE_ENV` at read time. When `'production'`, every
   * `responseGuidanceSource` in the `categories` array is
   * `'system_prompts'` (the stub module returns `null` in production);
   * the inspector can use this to gate the "non-prod stub source"
   * badge. Exposed at the top level so a UI doesn't have to inspect
   * every category to decide whether to render the badge.
   */
  readonly nodeEnv: 'development' | 'staging' | 'production';
  readonly categories: readonly ClassifierCategoryInspectorEntry[];
}

// Wire schema for CGC review submission. The CGC dashboard POSTs to
// `/api/cgc/classifier-categories/:key/review` with optional commentary.
export const SubmitClassifierCategoryReviewSchema = z.object({
  notes: z.string().trim().max(2_000).optional(),
});
export type SubmitClassifierCategoryReviewBody = z.infer<
  typeof SubmitClassifierCategoryReviewSchema
>;
